import { useCallback, useEffect, useState } from 'react'
import type { BillingRole, LabourCode, TimeEntryStatus, WorkPackage } from '@api-sdk'
import { useSsoToken } from './useSsoToken'
import { getBillingRoles, getLabourCodes, getTimeEntryStatuses, getWorkPackages } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import { toUtcDateOnly } from '../utils/formatters'
import type { ApiSlice } from './useClaimDetails'

const emptySlice = <T>(): ApiSlice<T> => ({ data: null, loading: false, error: null })
const loadingSlice = <T>(): ApiSlice<T> => ({ data: null, loading: true, error: null })

// Anything earlier is a year still being typed, not a day anyone worked. The form validates
// and caps its date field against the same bound.
export const MIN_BOOKABLE_DATE = '1900-01-01'

export interface TimeEntryOptions {
  workPackages: ApiSlice<WorkPackage[]>
  labourCodes: ApiSlice<LabourCode[]>
  billingRoles: ApiSlice<BillingRole[]>
  billingStatuses: ApiSlice<TimeEntryStatus[]>
  // Re-runs every fetch — one button covers whichever failed.
  retry: () => void
}

/**
 * The four Replicon-backed dropdowns on the Time Entry form (MSD-355).
 *
 * Work packages are per claim. Labour codes hang off the chosen work package, so nothing
 * is fetched until one is selected. Billing roles are resolved against the timesheet
 * covering the form's date, so changing the date refetches them. Billing statuses are the
 * same list everywhere, so they are fetched once.
 *
 * `maxDate` (yyyy-mm-dd) is the latest bookable day. Billing roles are only fetched for a
 * date between 1900 and it, because reading them creates a Replicon timesheet.
 */
export function useTimeEntryOptions(
  claimNumber: string,
  workPackageId: string,
  date: string,
  maxDate: string
): TimeEntryOptions {
  const { getToken } = useSsoToken()
  const [workPackages, setWorkPackages] = useState<ApiSlice<WorkPackage[]>>(emptySlice)
  const [labourCodes, setLabourCodes] = useState<ApiSlice<LabourCode[]>>(emptySlice)
  const [billingRoles, setBillingRoles] = useState<ApiSlice<BillingRole[]>>(emptySlice)
  const [billingStatuses, setBillingStatuses] = useState<ApiSlice<TimeEntryStatus[]>>(emptySlice)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setWorkPackages(loadingSlice)
      try {
        const token = await getToken()
        const data = await getWorkPackages(claimNumber, token)
        if (!cancelled) setWorkPackages({ data, loading: false, error: null })
      } catch (err) {
        if (cancelled) return
        console.error('[TimeEntryOptions] Work packages failed:', err)
        setWorkPackages({ data: null, loading: false, error: getApiErrorMessage(err, 'Failed to load work packages.') })
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [claimNumber, getToken, reloadKey])

  useEffect(() => {
    let cancelled = false

    async function load() {
      // Labour codes only exist under a work package — nothing to fetch until one is picked.
      if (!workPackageId) {
        setLabourCodes(emptySlice)
        return
      }
      setLabourCodes(loadingSlice)
      try {
        const token = await getToken()
        const data = await getLabourCodes(claimNumber, workPackageId, token)
        if (!cancelled) setLabourCodes({ data, loading: false, error: null })
      } catch (err) {
        if (cancelled) return
        console.error('[TimeEntryOptions] Labour codes failed:', err)
        setLabourCodes({ data: null, loading: false, error: getApiErrorMessage(err, 'Failed to load labor codes.') })
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [claimNumber, workPackageId, getToken, reloadKey])

  useEffect(() => {
    let cancelled = false

    async function load() {
      // The date is required, so a cleared or unparsable one has nothing to fetch against.
      // Sending none used to mean "today in UTC" server-side; it is now a 400, and showing
      // "Failed to load billing roles" for a field the user simply hasn't filled in yet would
      // read as a fault rather than an empty state.
      //
      // A date that can't be booked is skipped too: reading roles creates a timesheet, and a
      // native date input reports every intermediate year while one is typed (0002, 0020,
      // 0202…), so each of those would leave a timesheet behind. yyyy-mm-dd compares as a string.
      const bookable = date >= MIN_BOOKABLE_DATE && date <= maxDate
      const on = bookable ? toUtcDateOnly(date) : undefined
      if (!on) {
        setBillingRoles(emptySlice)
        return
      }
      setBillingRoles(loadingSlice)
      try {
        const token = await getToken()
        const data = await getBillingRoles(claimNumber, token, on)
        if (!cancelled) setBillingRoles({ data, loading: false, error: null })
      } catch (err) {
        if (cancelled) return
        console.error('[TimeEntryOptions] Billing roles failed:', err)
        setBillingRoles({ data: null, loading: false, error: getApiErrorMessage(err, 'Failed to load billing roles.') })
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [claimNumber, date, maxDate, getToken, reloadKey])

  useEffect(() => {
    let cancelled = false

    async function load() {
      setBillingStatuses(loadingSlice)
      try {
        const token = await getToken()
        const data = await getTimeEntryStatuses(token)
        if (!cancelled) setBillingStatuses({ data, loading: false, error: null })
      } catch (err) {
        if (cancelled) return
        console.error('[TimeEntryOptions] Billing statuses failed:', err)
        setBillingStatuses({
          data: null,
          loading: false,
          error: getApiErrorMessage(err, 'Failed to load billing statuses.'),
        })
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [getToken, reloadKey])

  const retry = useCallback(() => setReloadKey((key) => key + 1), [])

  return { workPackages, labourCodes, billingRoles, billingStatuses, retry }
}
